import { createFileRoute } from "@tanstack/react-router";
import { PolicyLayout } from "@/components/PolicyLayout";
import { ConsumerRightsNotice } from "@/components/policies/ConsumerRightsNotice";

export const Route = createFileRoute("/policies/money-back-promise")({
  component: MoneyBackPromise,
  head: () => ({
    meta: [
      { title: "60-Day TruWe Money-Back Promise" },
      {
        name: "description",
        content:
          "Terms of the 60-Day TruWe Money-Back Promise: who is covered, how to claim, and what is refunded on eligible first purchases where the Promise was offered.",
      },
      { property: "og:title", content: "60-Day TruWe Money-Back Promise" },
      {
        property: "og:description",
        content:
          "Give the protocol a fair try. If you are not satisfied within 60 days, you are covered under our satisfaction policy.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [
      { rel: "canonical", href: "https://truwe.in/policies/money-back-promise" },
    ],
  }),
});

function MoneyBackPromise() {
  return (
    <PolicyLayout title="60-Day TruWe Money-Back Promise" updated="4 October 2026">
      <p>
        Give the protocol a fair try. If you&rsquo;re not satisfied with your experience
        within 60 days, you&rsquo;re covered under our satisfaction policy. This page
        explains exactly what that means, so there are no surprises later.
      </p>

      <ConsumerRightsNotice />
      <p>Coverage follows the Promise offered with your product or bundle when you ordered.
        Liver Biome Plus and Mag Absorb Pro are the examples below. For Ova Balance or a mixed
        bundle, check the specific offer or ask support before purchase. Existing advertised
        promises remain honoured; this clarification does not remove historical eligibility.</p>
      <h2>1. Who is covered</h2>
      <ul>
        <li>
          The Promise applies to your <strong>first purchase of an eligible product where this Promise was offered</strong>{" "}
          (for example, your first Liver Biome Plus order or your first Mag Absorb Pro
          order).
        </li>
        <li>
          A maximum of <strong>one claim per customer, household and product</strong>.
        </li>
        <li>
          Orders placed directly on truwe.in. Purchases through marketplaces or resellers
          follow that platform&rsquo;s policy.
        </li>
      </ul>

      <h2>2. Give it a fair try</h2>
      <p>
        Nutrition works over weeks, not days. We ask that you use the product{" "}
        <strong>consistently for at least 30 days</strong> before raising a claim. The only
        exceptions are intolerance, an adverse reaction, or a genuine safety concern &mdash;
        in those cases, stop use and contact us immediately.
      </p>

      <h2>3. Claim window</h2>
      <p>
        Raise your request within <strong>60 days of delivery</strong>. Requests after 60
        days fall outside the Promise, though our normal{" "}
        <a href="/policies/refund-policy">Refund Policy</a> and{" "}
        <a href="/policies/return-exchange-policy">Return &amp; Exchange Policy</a> still
        apply for damaged, defective or incorrect items.
      </p>

      <h2>4. What we need from you</h2>
      <ul>
        <li>Proof of order (order ID or confirmation email).</li>
        <li>A short reason describing what didn&rsquo;t work for you.</li>
        <li>
          For the 3-bottle 90-Day Inaugural Protocol, at least{" "}
          <strong>one unopened bottle must be returned</strong> for an ordinary satisfaction claim.
          This condition does not override the early safety exception or mandatory remedies;
          contact support for safe return instructions in those cases.
        </li>
      </ul>

      <h2>5. What is refunded</h2>
      <ul>
        <li>
          We refund the <strong>net amount you actually paid</strong> for the product after
          discounts, offers and any loyalty credit applied at checkout.
        </li>
        <li>
          For a voluntary satisfaction claim, disclosed shipping and return charges are normally
          excluded. This does not apply where reimbursement is required for defective, unsafe,
          incorrect or misdescribed goods or otherwise by law. Any return cost is explained before collection.
        </li>
        <li>
          Loyalty credit earned on the order is reversed, and any coupon used is treated as
          consumed.
        </li>
        <li>
          Refunds are processed to the original payment method within 5&ndash;7 business
          days of approval. For COD orders, we collect your bank details to process a
          transfer.
        </li>
      </ul>

      <h2>6. How to claim</h2>
      <p>
        Email <a href="mailto:hello@truwe.in">hello@truwe.in</a> with your order ID and
        reason. We acknowledge consumer complaints within 48 hours, address them within one month and arrange pickup where
        a bottle needs to be returned.
      </p>

      <h2>7. What this Promise is not</h2>
      <p>
        This is a satisfaction policy, not a health outcome guarantee. TruWe makes no claim
        that any product will produce specific liver, sleep, magnesium, hormonal or clinical
        results. Supplements support a broader routine of diet, sleep, movement and medical
        care. If you have a medical condition, are pregnant or nursing, or take prescription
        medication, consult your doctor before use.
      </p>

      <h2>8. Fair use</h2>
      <p>
        We may decline claims where there is evidence of abuse &mdash; for example repeated
        claims across accounts or addresses, bulk or resale purchases, or claims where the
        required proof isn&rsquo;t provided. TruWe may update these terms; the version shown
        at the time of your order applies to that order.
      </p>
    </PolicyLayout>
  );
}
